/**
 * appOnboardingStyles.js
 *
 * All styled-components for the AppOnboarding module.
 *
 * ⚠️  PLACEHOLDER build — structural skeleton only.
 *     Visual styles will be applied once the design CSS is provided.
 *     Every export here is already imported by AppOnboardingPanel,
 *     AppOnboardingExpanded, and StepCard so the app compiles cleanly.
 */
import styled from "styled-components";



import { TABLET_MAX, TABLET_MIN } from "../../../styles/breakpoints";
import { motion } from "../../AuthNew/shared/motionUtils";


// ---------------------------------------------------------------------------
// Framer Motion animation variants
// ---------------------------------------------------------------------------

export const panelSlideUp = {
  initial: { y: "100%" },
  animate: { y: 0, transition: { type: "spring", stiffness: 180, damping: 28 } },
  exit: { y: "100%", transition: { duration: 0.35, ease: "easeIn" } },
};

export const stepSlideIn = {
  initial: { opacity: 0, x: 30 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.45, ease: "easeOut" } },
  exit: { opacity: 0, x: -30, transition: { duration: 0.28, ease: "easeIn" } },
};

// ---------------------------------------------------------------------------
// Bottom panel strip (collapsed)
// ---------------------------------------------------------------------------

export const BottomPanelWrapper = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: #ffffff;
  border-top: 1px solid #e9ecef;
  box-shadow: 0 -8px 50px 0 rgba(0, 14, 42, 0.15);
  border-radius: 16px 16px 0 0;
  border-top: 1px solid #2266ef;
  padding: 12px 64px;

  @media (max-width: ${TABLET_MAX}px) {
    bottom: 72px;
    padding: 12px;
    border-radius: 12px 12px 0 0;
    box-shadow: 0px -4px 25px rgba(0, 14, 42, 0.2);
  }
`;

export const PanelInner = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  gap: 24px; /* Reduced from 80px */
  width: 100%;
  height: 78px;
  order: 0;
  align-self: stretch;
  flex-grow: 1;
`;

export const PanelLeftGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 24px;
  width: auto;
  height: 78px;
  flex: 1; /* Dynamic */
  order: 0;
  flex-grow: 1;
  min-width: 0;
`;

export const BadgeAndInfoGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 16px;
  width: auto; /* Dynamic */
  min-width: 0;
  height: 78px;
  flex: 1; /* Grow as needed */
  order: 0;
  flex-grow: 1;
`;

export const StepBadge = styled.div`
  position: relative;
  width: 60px;
  height: 60px;
  flex: none;
  order: 0;
  flex-grow: 0;
  flex-shrink: 0;
`;

export const StepBadgeBackground = styled.div`
  position: absolute;
  width: 60px;
  height: 60px;
  left: 0;
  top: 0;
  background: #EDF8FE;
  border-radius: 50%;
`;

export const StepBadgeProgress = styled.div`
  position: absolute;
  width: 60px;
  height: 60px;
  left: 0;
  top: 0;
  background: conic-gradient(from 0deg, #2266EF 0deg, #2266EF ${({ $percent }) => $percent * 3.6}deg, transparent ${({ $percent }) => $percent * 3.6}deg);
  border-radius: 50%;
  transform: rotate(-90deg);
  z-index: 1;
`;

export const StepBadgeNumber = styled.span`
  position: absolute;
  width: 9px;
  height: 27px;
  left: 15.02px;
  top: 16px;
  font-style: normal;
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000E2A;
  z-index: 2;
`;

export const StepBadgeTotal = styled.span`
  position: absolute;
  width: 14px;
  height: 21px;
  left: 28.47px;
  top: 20px;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 21px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666E7F;
  z-index: 2;
`;

export const PanelStepInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 4px;
  width: auto; /* Dynamic */
  min-width: 0;
  height: 78px;
  flex: 1;
  order: 1;
  flex-grow: 1;
  justify-content: center;
`;

export const PanelStepTitle = styled.p`
  margin: 0;
  width: max-content;
  height: 28px;
  font-size: 20px;
  font-weight: 800;
  color: #000e2a;
  line-height: 28px;
  letter-spacing: -0.5px;
  font-style: normal;
  flex: none;
  order: 0;
  flex-grow: 0;
`;

export const PanelStepSubtitle = styled.p`
  margin: 0;
  width: 100%; /* Dynamic */
  max-width: 500px;
  height: auto;
  font-size: 16px;
  line-height: 23px;
  color: #353a46;
  font-weight: 400;
  display: flex;
  align-items: center;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ContinueButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 40px;
  gap: 8px;
  width: 143px;
  height: 48px;
  background: linear-gradient(90deg, #2266EF 0%, #6C2EF2 100%);
  border-radius: 32px;
  border: none;
  cursor: pointer;
  flex: none;
  order: 1;
  flex-grow: 0;
  font-weight: 700;
  font-size: 14px;
  line-height: 21px;
  color:#FFFFFF;
  text-align: center;
  cursor: pointer;

  @media (max-width: ${TABLET_MAX}px) {
    width: 100px; /* Figma spec */
    height: 40px; /* Figma spec */
    padding: 8px 16px; /* Figma spec */
  }
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const PanelRightGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 24px;
  width: auto;
  height: 64px;
  flex: none; /* Keep right group compact */
  order: 1;
  flex-grow: 0;
`;

export const TimerBox = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 12px;
  gap: 12px;
  width: ${({ isMobileView }) => (isMobileView ? "auto" : "max-content")};
  min-width: 0;
  height: 64px;
  background: linear-gradient(90deg, #EDF8FE 0%, #F4ECFE 100%);
  /* border: 1px solid #C6B4F9; */
  border-radius: 16px;
  flex-shrink: 1;
  order: 0;
  flex-grow: 0;
`;

export const TimerDivider = styled.div`
  width: 1px;
  height: 32px;
  background: #CFBBFB;
  border-radius: 0px;
  flex: none;
  order: 2;
  flex-grow: 0;
`;

export const TimerValueWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px 12px;
  gap: 10px;
  width: 115px;
  height: 40px;
  background: #FFFFFF;
  border: 1px solid #CFBBFB;
  border-radius: 8px;
  flex: none;
  order: 3;
  flex-grow: 0;
`;

export const TimerValue = styled.span`
  width: 91px;
  height: 27px;
  font-style: normal;
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  letter-spacing: 1px;
  color: ${({ isExpired }) => (isExpired ? "#A0A3A9" : "#6C2EF2")};
  flex: none;
  order: 0;
  flex-grow: 0;
`;

export const GiftIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  order: 0;
  flex: none;
  flex-grow: 0;
`;

export const CreditCopy = styled.div`
  box-sizing: border-box;
  width: min(auto, auto);
  max-width: 100%;
  word-break: break-word;
  overflow-wrap: anywhere;
  white-space: normal;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #353A46;
  flex: none;
  order: 1;
  flex-grow: 0;
`;

export const CreditHighlight = styled.span`
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: #6C2EF2;

`;

export const ChevronButton = styled.button`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 10px;
  gap: 10px;
  width: 48px;
  height: 48px;
  border: 1px solid #D3E0FC;
  border-radius: 40px;
  background: none;
  cursor: pointer;
  flex-shrink: 0;
  transition: transform 0.25s ease;
  transform: ${({ $isExpanded, isMobileView }) =>
    $isExpanded ? (isMobileView ? "rotate(180deg)" : "rotate(0deg)") : "rotate(180deg)"};
  flex: none;
  order: 1;
  flex-grow: 0;

  &:hover {
    background-color: rgba(211, 224, 252, 0.2);
  }
`;

export const ChevronIcon = styled.img`
  width: 15.84px;
  height: 7.1px;
  flex: none;
  order: 0;
  flex-grow: 0;
`;

// ---------------------------------------------------------------------------
// Mobile-Specific Trial Banner (Refactored)
// ---------------------------------------------------------------------------

export const MobileTrialBanner = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px;
  gap: 12px;
  width: 100%;
  min-height: 56px;
  background: linear-gradient(90deg, #EDF8FE 0%, #F4ECFE 100%);
  border-radius: 12px;
  flex: none;
  order: 1;
  flex-grow: 1;
  margin: 24px 0 0 0;
`;

export const MobileGiftIcon = styled.div`
  width: 40px;
  height: 40px;
  background: #ffffff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  order: 0;
  flex-grow: 0;

  /* img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  } */
`;

export const MobileCreditText = styled.div`
  flex: 1;
  min-width: 0;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #353a46;
  order: 1;
`;

export const MobileCollapseButton = styled.button`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 10px;
  gap: 10px;
  width: 36px;
  height: 36px;
  background: #ffffff;
  border: 1px solid #d3e0fc;
  border-radius: 40px;
  flex: none;
  order: 2;
  flex-grow: 0;
  cursor: pointer;
  transition: transform 0.25s ease;
  transform: ${({ $isExpanded }) => ($isExpanded ? "rotate(0deg)" : "rotate(180deg)")};

  &:hover {
    background-color: rgba(211, 224, 252, 0.2);
  }
`;

export const MobileChevronIcon = styled.img`
  width: 15.84px;
  height: 7.1px;
  flex: none;
  order: 0;
  flex-grow: 0;
`;

// ---------------------------------------------------------------------------
// Mobile-Specific Minimized Panel (Refactored)
// ---------------------------------------------------------------------------

export const MobileMinimizedPanel = styled(motion.div)`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 12px;
  gap: 8px;
  position: fixed; /* Was absolute in spec, but fixed is usually better for bottom bars */
  left: 0;
  right: 0;
  bottom: 71px;
  width: 100%;
  background: #ffffff;
  box-shadow: 0px -4px 25px rgba(0, 14, 42, 0.2);
  border-radius: 12px 12px 0px 0px;
  z-index: 1000;
`;

export const MobileMinimizedHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  gap: 8px;
  width: 100%;
  height: 32px;
  align-self: stretch;
`;

export const MobileSmallBadge = styled.div`
  position: relative;
  width: 32px;
  height: 32px;
  flex: none;
  order: 0;
  flex-grow: 0;
`;

export const MobileTimerChip = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px 8px;
  gap: 10px;
  width: 82px;
  height: 32px;
  background: #ffffff;
  border: 1px solid #cfbbfb;
  border-radius: 8px;
  flex: none;
  order: 1;
  flex-grow: 0;

  span {
    width: 66px;
    height: 20px;
    font-style: normal;
    font-weight: 800;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 1px;
    color: ${({ isExpired }) => (isExpired ? "#A0A3A9" : "#6c2ef2")};
    text-align: center;
  }
`;

export const MobileBadgeNumber = styled.span`
  position: absolute;
  width: 4.8px;
  height: 14.4px;
  left: 7.01px;
  top: 8.53px;

  font-style: normal;
  font-weight: 800;
  font-size: 12px;
  line-height: 27px;
  display: flex;
  align-items: center;
  color: #000e2a;
`;

export const MobileBadgeTotal = styled.span`
  position: absolute;
  width: 12px;
  height: 21px;
  left: 13.18px;
  top: 5.67px;

  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 21px;
  display: flex;
  align-items: center;
  color: #666e7f;
`;

// ---------------------------------------------------------------------------
// Tablet-Specific Minimized Panel (Refactored)
// ---------------------------------------------------------------------------

export const TabletMinimizedPanel = styled(motion.div)`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 8px;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 72px;
  width: 100%;
  height: 96px;
  background: #ffffff;
  box-shadow: 0px -4px 25px rgba(0, 14, 42, 0.2);
  border-radius: 16px 16px 0px 0px;
  z-index: 1000;
`;

export const TabletHeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  gap: 24px;
  width: 100%;
  height: 64px;
  align-self: stretch;
`;

export const TabletSmallBadge = styled.div`
  position: relative;
  width: 60px;
  height: 60px;
  flex: none;
  order: 0;
  flex-grow: 0;
`;

export const TabletBadgeNumber = styled.span`
  position: absolute;
  width: 9px;
  height: 27px;
  left: 15.02px;
  top: 18px;

  font-style: normal;
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  display: flex;
  align-items: center;
  color: #000e2a;
`;

export const TabletBadgeTotal = styled.span`
  position: absolute;
  width: 14px;
  height: 21px;
  left: 28.47px;
  top: 22px;

  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 21px;
  display: flex;
  align-items: center;
  color: #666e7f;
`;

export const TabletBanner = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 12px 8px 8px;
  gap: 12px;
  width: auto;
  height: 64px;
  background: linear-gradient(90deg, #edf8fe 0%, #f4ecfe 100%);
  border-radius: 16px;
  flex: 1;
  order: 1;
  flex-grow: 1;
`;

export const TabletGiftIcon = styled.div`
  width: 48px;
  height: 48px;
  background: #ffffff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  order: 0;
  flex-grow: 0;

  /* img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  } */
`;

export const TabletDivider = styled.div`
  width: 1px;
  height: 32px;
  background: #cfbbfb;
  flex: none;
  order: 2;
  flex-grow: 0;
`;

export const TabletTimerChip = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px 12px;
  gap: 10px;
  width: 115px;
  height: 40px;
  background: #ffffff;
  border: 1px solid #cfbbfb;
  border-radius: 8px;
  flex: none;
  order: 3;
  flex-grow: 0;

  span {
    width: 91px;
    height: 27px;
    font-style: normal;
    font-weight: 800;
    font-size: 20px;
    line-height: 27px;
    letter-spacing: 1px;
    color: ${({ isExpired }) => (isExpired ? "#A0A3A9" : "#6c2ef2")};
    text-align: center;
  }
`;

export const TabletCollapseButton = styled.button`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 10px;
  gap: 10px;
  width: 48px;
  height: 48px;
  background: #ffffff;
  border: 1px solid #d3e0fc;
  border-radius: 40px;
  flex: none;
  order: 2;
  flex-grow: 0;
  cursor: pointer;
  transition: transform 0.25s ease;
  transform: ${({ $isExpanded }) => ($isExpanded ? "rotate(0deg)" : "rotate(180deg)")};

  &:hover {
    background-color: rgba(211, 224, 252, 0.2);
  }
`;

// ---------------------------------------------------------------------------
// Expanded overlay container
// ---------------------------------------------------------------------------

export const ExpandedContainer = styled(motion.div)`
  position: fixed;
  bottom: var(
    --app-onboarding-panel-height,
    0px
  ); /* tracks the strip height so no gap remains when strip is hidden */
  left: 0;
  right: 0;
  width: 100%;
  z-index: 999;
  box-sizing: border-box;
  background: #ffffff;
  /* background-color:green; */
  border-top: 1px solid #e9ecef;
  box-shadow: 0 -8px 50px 0 rgba(0, 14, 42, 0.15);
  border-radius: 16px 16px 0 0;
  border-top: 1px solid #2266ef;
  max-height: 80vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px 64px;

  @media (max-width: ${TABLET_MAX}px) {
    bottom: 72px;
    padding: 16px;
    border-radius: 16px 16px 0 0;
    box-shadow: 0px -4px 25px rgba(0, 14, 42, 0.2);
    height: auto;
    max-height: calc(100vh - 152px);
    overflow-y: auto;
    width: 100%;
    max-width: 100vw;
    left: 0;
    transform: none;
    gap: 24px;
  }
`;

export const ExpandedInner = styled.div`
  padding: 0px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
`;

export const ExpandedHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: ${TABLET_MAX}px) {
    width: 100%;
    justify-content: space-between;
    align-items: flex-start;
    padding: 0;
    gap: 23px;
    height: 53px;
    margin-bottom: 24px;
  }
`;

export const ExpandedHeaderTitle = styled.div`
  flex: 1;
`;

export const ExpandedTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #000e2a;
`;

export const ExpandedSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: #353a46;
`;

// ---------------------------------------------------------------------------
// Progress bar
// ---------------------------------------------------------------------------

export const ProgressBarTrack = styled.div`
  width: 100%;
  height: 1px;
  background: #D3E0FC;
  border-radius: 4px;
  margin-bottom: 28px;
  overflow: hidden;
`;

export const ProgressBarFill = styled.div`
  height: 100%;
  border-radius: 4px;
  /* background: linear-gradient(90deg, #2266ef 0%, #6c2ef2 100%); */
  background-color: #D3E0FC;
  /* transition: width 0.4s ease; */
  width: ${({ $percent }) => $percent}%;
`;

// ---------------------------------------------------------------------------
// Step dots row
// ---------------------------------------------------------------------------

export const StepDotsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 0;
  justify-content: space-between;

  width: 100%;
  height: 61px;

  flex: 1;
  order: 4;
  align-self: stretch;
  flex-grow: 1;

  @media (max-width: ${TABLET_MAX}px) {
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    height: auto;
    padding-bottom: 24px;
  }
`;

export const StepDotWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: ${({ isMobileView }) => isMobileView ? "5px" : "12px"};

  flex: 1;
  height: 61px;
  position: relative;

  background-color: transparent;

  flex-grow: 1;
  flex-shrink: 1;
  min-width: auto;

  ${({ $showConnector }) =>
    $showConnector
      ? `
    &::after {
      content: "";
      position: absolute;
      top: 15px;
      left: calc(50% + 16px);
      width: calc(100% + 23px - 32px);
      height: 1px;
      // background: linear-gradient(90deg, #2266EF 0%, #6C2EF2 100%);
      background-color: #D3E0FC;
      border-radius: 1px;
      pointer-events: none;
      z-index: 0;
    }
  `
      : ""}
`;

export const StepDot = styled.button`
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid
    ${({ $state }) =>
      $state === "active" ||
      $state === "completed" ||
      $state === "completed-active" ||
      $state === "skipped-active"
        ? "#2266EF"
        : "#D3E0FC"};
  background: #ffffff;
  width: 32px;
  height: 32px;
  border-radius: 16px;

  @media (max-width: ${TABLET_MAX}px) {
    width: 32px;
    height: 32px;
    border-radius: 16px;
    margin-bottom: 1px;

    &::before {
      width: 22px;
      height: 22px;
    }

    > span {
      font-size: 13px;
    }
  }
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  outline: none;
  position: relative;
  isolation: isolate;
  z-index: 1;
  transition: all 0.2s ease;

  /* Outer ring for completed-active — shows it is currently selected */
  box-shadow: ${({ $state }) =>
    $state === "completed-active"
      ? "0 0 0 3px rgba(34, 102, 239, 0.25)"
      : "none"};

  &::before {
    content: "";
    width: 22px;
    height: 22px;
    border-radius: 11px;
    background: ${({ $state }) =>
      $state === "active" ||
      $state === "completed" ||
      $state === "completed-active" || $state === "skipped-active"
        ? "#2266EF"
        : "transparent"};
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 0;
    transition: background 0.2s ease;
  }

  > span {
    position: relative;
    z-index: 1;
    font-weight: ${({ $state }) =>
      $state === "active" ||
      $state === "completed" ||
      $state === "completed-active" || $state === "skipped-active"
        ? 800
        : 600};
    transition: font-weight 0.2s ease;
  }

  > svg {
    position: relative;
    z-index: 2;
  }

  color: ${({ $state }) =>
    $state === "active" ||
    $state === "completed" ||
    $state === "completed-active" ||
    $state === "skipped-active"
      ? "#ffffff"
      : "#666E7F"};
  font-size: 13px;
  line-height: 19.5px;
  font-weight: ${({ $state }) =>
    $state === "active" ||
    $state === "completed" ||
    $state === "completed-active" ||
    $state === "skipped-active"
      ? 800
      : 600};

  &:hover:not(:active) {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(34, 102, 239, 0.2);
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(34, 102, 239, 0.2);
  }
`;

export const StepDotLabel = styled.span`
  /* Overflow the 32px wrapper so full text is visible, centered under the dot */
  width: max-content;
  max-width: 120px;
  height: 17px;
  
  font-style: normal;
  font-size: 12px;
  line-height: 17px;
  text-align: center;
  
  flex: none;
  flex-grow: 0;
  
  white-space: nowrap;
  overflow: visible;

  @media (max-width: ${TABLET_MAX}px) {
    position: absolute;
    top: 38px;
    left: 50%;
    transform: translateX(-50%);
  }
  
  ${({ $state }) => {
    switch ($state) {
      case "active":
      case "completed-active":
      case "skipped-active":
        return `
          font-weight: 700;
          color: #000E2A;
        `;
      default: // pending or completed
        return `
          font-weight: 500;
          color: #666E7F;
        `;
    }
  }}
`;

// ---------------------------------------------------------------------------
// Step card layout
// ---------------------------------------------------------------------------

export const StepCardWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  /* gap: 84px; */
  
  width: 100%;
  height: 280px;

  flex: none;
  order: 2;
  align-self: stretch;
  flex-grow: 0;

  @media (max-width: ${TABLET_MAX}px) {
    flex-direction: column;
    gap: 40px;
    height: auto;
    align-items: flex-start; /* Default for mobile/tablet */

    @media (min-width: ${TABLET_MIN}px) {
      align-items: center; /* Center only on tablet */
    }
  }
`;

export const StepTextCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 32px;

  width: 614px;
  height: max-content;

  flex: none;
  order: 0;
  flex-grow: 1;

  @media (max-width: ${TABLET_MAX}px) {
    width: 369px; /* Figma spec */
    max-width: 100%;
    height: auto; /* Allow auto height on mobile */
    gap: 24px;
    order: 1;

    /* Center on tablet, flex-start on mobile */
    align-self: flex-start;

    @media (min-width: ${TABLET_MIN}px) {
      height: auto;
      align-self: center;
    }

    /* Internal alignment */
    align-items: flex-start;
    text-align: left;
  }
`;

/* Groups StepTitleSubGroup + StepDescription together with 16px gap */
export const StepTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;

  width: ${({ width }) => width || "614px"};
  height: 134px;
  box-sizing: border-box;
  max-width: 100%;
  overflow-x: hidden;

  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;

  @media (max-width: ${TABLET_MAX}px) {
    width: 369px; /* Figma spec */
    max-width: 100%;
    height: auto;
    gap: 16px;
    align-items: flex-start;

    @media (min-width: ${TABLET_MIN}px) {
      height: 116px; /* Precise height for tablet */
    }
  }
`;

/* Groups StepLabel + StepTitle together with 8px gap */
export const StepTitleSubGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;

  width: 614px;
  height: 72px;
  box-sizing: border-box;
  max-width: 100%;
  overflow: hidden;

  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;

  @media (max-width: ${TABLET_MAX}px) {
    width: 369px; /* Figma spec */
    max-width: 100%;
    height: auto;
    gap: 8px;
    align-items: flex-start;

    @media (min-width: ${TABLET_MIN}px) {
      height: 58px; /* Precise height for tablet */
    }
  }
`;

export const StepMediaCol = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  
  width: 614px;
  height: 280px;
  
  flex: none;
  order: 1;
  flex-grow: 0;
  
  position: relative;

  @media (max-width: ${TABLET_MAX}px) {
    width: 100%;
    height: auto;
    order: 0;
  }
`;

export const StepLabel = styled.span`
  display: inline-block;
  width: 100%;
  height: 21px;
  
  font-family: 'Merriweather';
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 21px;
  
  color: #2266EF;
  
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;

  @media (max-width: ${TABLET_MAX}px) {
    width: 369px;
  }
`;

export const StepTitle = styled.h2`
  margin: 0;
  width: auto;
  height: 45px;
  
  font-style: normal;
  font-weight: 700;
  font-size: 32px;
  line-height: 45px;
  
  color: #000E2A;
  
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;

  @media (max-width: ${TABLET_MAX}px) {
    width: 369px; /* Figma spec */
    max-width: 100%;
    height: auto;
    font-size: 24px;
    line-height: 120%;

    @media (min-width: ${TABLET_MIN}px) {
      height: 29px; /* Precise height for tablet */
    }
  }
`;

export const StepDescription = styled.p`
  margin: 0;
  width: 361px;
  height: 46px;

  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 23px;

  color: #353A46;

  flex: none;
  order: 1;
  flex-grow: 0;

  @media (max-width: ${TABLET_MAX}px) {
    width: 369px; /* Figma spec */
    max-width: 100%;
    height: auto;
    font-size: 14px;
    line-height: 150%;

    @media (min-width: ${TABLET_MIN}px) {
      height: 42px; /* Precise height for tablet */
    }
  }
`;

export const StepButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 22px;

  width: 223px;
  height: 48px;

  flex: none;
  order: 1;
  flex-grow: 0;

  @media (max-width: ${TABLET_MAX}px) {
    width: 369px; /* Figma spec */
    height: 40px; /* Figma spec */
    gap: 22px; /* Figma spec */
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: 12px;
    height: auto;
  }
`;

export const CtaButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 32px;
  gap: 8px;
  
  width: max-content;
  height: 48px;

  background: linear-gradient(90deg, #2266EF 0%, #6C2EF2 100%);
  border-radius: 32px;
  border: none;
  
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 21px;
  color: #FFFFFF;
  
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;

  @media (max-width: ${TABLET_MAX}px) {
    width: 247px; /* Figma spec */
    height: 40px; /* Figma spec */
    padding: 8px 32px; /* Figma spec */
    flex-grow: 1;
    order: 1;
  }
  
  flex: none;
  order: 1;
  flex-grow: 0;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const SkipLink = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  gap: 8px;

  width: 63px;
  height: 40px;

  background: transparent;
  border: none;
  border-radius: 32px;

  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 21px;
  color: #666e7f;

  cursor: pointer;
  transition: opacity 0.2s ease;

  flex: none;
  order: 0;
  flex-grow: 0;

  @media (max-width: ${TABLET_MAX}px) {
    width: 100px; /* Figma spec */
    height: 40px; /* Figma spec */
    padding: 8px 16px; /* Figma spec */
    flex-grow: 0;
    order: 0;
  }

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const MediaArea = styled.div`
  position: relative;
  width: 614px;
  height: 280px;

  background: #FAF5F2;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  flex: none;
  order: 1;
  flex-grow: 0;

  @media (max-width: ${TABLET_MAX}px) {
    width: 100%;
    height: 221px;
    background: #EDF8FE;
  }
`;

export const PlayButtonOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0);
  cursor: pointer;
  transition: background-color 0.3s ease;
  top: 0;
  left: 0;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

export const PlayButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px;
  gap: 10px;

  width: 80px;
  height: 80px;

  background: linear-gradient(
    138.69deg,
    rgba(34, 102, 239, 0.65) 18.15%,
    rgba(108, 46, 242, 0.65) 78.01%
  );
  border-radius: 48px;

  flex: none;
  order: 1;
  flex-grow: 0;
  z-index: 1;

  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: ${TABLET_MAX}px) {
    width: 64px;
    height: 64px;
    border-radius: 32px;
  }
`;

export const PlayIcon = styled.div`
  width: 0;
  height: 0;
  border-top: 14px solid transparent;
  border-bottom: 14px solid transparent;
  border-left: 22px solid #ffffff;
  margin-left: 4px;

  flex: none;
  order: 0;
  flex-grow: 0;

  @media (max-width: ${TABLET_MAX}px) {
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-left: 16px solid #ffffff;
    margin-left: 3px;
  }
`;

// ---------------------------------------------------------------------------
// Install Mobile App step — store badge layout
// ---------------------------------------------------------------------------

export const InstallAppButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 32px;
  width: auto;
  height: 104px;
  flex: none;
  flex-grow: 0;

  @media (max-width: ${TABLET_MAX}px) {
    width: max-content;
    height: auto;
    gap:0px;

  }
`;

export const AppLinksColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 248px;
  height: 104px;
  flex: none;
  flex-grow: 0;

  @media (max-width: ${TABLET_MAX}px) {
    width: 100%;
    height: auto;
  }
`;

export const AppLinkRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 248px;
  height: 44px;
  flex: none;
  flex-grow: 0;

  @media (max-width: ${TABLET_MAX}px) {
    width: 100%;
  }
`;

export const StoreBadgeLink = styled.a`
  display: block;
  flex-shrink: 0;
  text-decoration: none;
`;

export const StoreBadge = styled.img`
  height: 44px;
  display: block;
`;

export const CopyLinkButton = styled.button`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px 16px;
  width: 96px;
  height: 44px;
  background: #ffffff;
  border: 1px solid #d3e0fc;
  border-radius: 32px;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: #2266ef;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: #f0f5ff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export const InstallSkipButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 14px;
  color: #666e7f;
  cursor: pointer;
  /* text-decoration: underline; */
  align-self: center;

  &:hover {
    color: #374151;
  }
`;